/**
 * QuestionSourceInfo (NQG-RQ-26 v2)
 * Compact "Source" section showing granular traceability back to the source
 * document: source file(s), section/OE code + learning objective, cognitive
 * taxonomy (Bloom level or action verbs) and explicit topic weighting.
 *
 * Used by the main question bank detail modal for questions synced from the
 * free-source PDF question bank.
 */
import { FileText, BookOpen, Brain, Percent } from 'lucide-react';

interface SourceDocument {
  documentId?: string;
  title?: string;
  filename?: string;
}

interface SourceRef {
  analysisId?: string;
  topicIndex?: number;
  code?: string;
  objectiveText?: string;
}

interface CognitiveTaxonomy {
  framework?: 'bloom' | 'action_verb';
  value?: string;
}

export interface QuestionSourceInfoProps {
  sourceDocuments?: SourceDocument[];
  sourceRef?: SourceRef;
  cognitiveTaxonomy?: CognitiveTaxonomy;
  topicWeighting?: number;
  className?: string;
}

export function hasQuestionSourceInfo(q: {
  sourceDocuments?: SourceDocument[];
  sourceRef?: SourceRef;
  cognitiveTaxonomy?: CognitiveTaxonomy;
  topicWeighting?: number;
}): boolean {
  return Boolean(
    (q.sourceDocuments && q.sourceDocuments.length > 0) ||
      (q.sourceRef && (q.sourceRef.code || q.sourceRef.objectiveText)) ||
      q.cognitiveTaxonomy?.value ||
      typeof q.topicWeighting === 'number'
  );
}

export function QuestionSourceInfo({
  sourceDocuments,
  sourceRef,
  cognitiveTaxonomy,
  topicWeighting,
  className = '',
}: QuestionSourceInfoProps) {
  const hasDocs = !!sourceDocuments && sourceDocuments.length > 0;
  const hasRef = !!sourceRef && !!(sourceRef.code || sourceRef.objectiveText);
  const hasTaxonomy = !!cognitiveTaxonomy?.value;
  const hasWeighting = typeof topicWeighting === 'number';

  if (!hasDocs && !hasRef && !hasTaxonomy && !hasWeighting) return null;

  return (
    <div className={`bg-gradient-to-r from-gray-50 to-indigo-50/20 rounded-xl p-4 border border-gray-200 ${className}`}>
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Source</p>
      <div className="space-y-2.5">
        {/* Source document filename(s) */}
        {hasDocs && (
          <div className="flex flex-wrap gap-2">
            {sourceDocuments!.map((doc, i) => (
              <span
                key={doc.documentId || i}
                className="inline-flex items-center gap-1.5 text-xs bg-white text-gray-700 rounded-lg px-3 py-1.5 border border-gray-200"
                title={doc.title || doc.filename}
              >
                <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                {doc.filename || doc.title}
              </span>
            ))}
          </div>
        )}

        {/* Section / OE code + learning objective */}
        {hasRef && (
          <div className="flex items-start gap-2">
            <BookOpen className="w-3.5 h-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              {sourceRef!.code && (
                <span className="inline-block font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200 mr-2">
                  {sourceRef!.code}
                </span>
              )}
              {sourceRef!.objectiveText && (
                <span className="text-xs text-gray-700">
                  <span className="font-semibold text-gray-500">Learning objective:</span>{' '}
                  {sourceRef!.objectiveText}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Cognitive taxonomy + topic weighting badges */}
        {(hasTaxonomy || hasWeighting) && (
          <div className="flex items-center gap-2 flex-wrap">
            {hasTaxonomy && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 bg-purple-50 px-2.5 py-1 rounded-lg border border-purple-200">
                <Brain className="w-3 h-3 flex-shrink-0" />
                {cognitiveTaxonomy!.framework === 'action_verb'
                  ? `Action verbs: ${cognitiveTaxonomy!.value}`
                  : `Bloom: ${cognitiveTaxonomy!.value}`}
              </span>
            )}
            {hasWeighting && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                <Percent className="w-3 h-3 flex-shrink-0" />
                Topic weight: {topicWeighting}%
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default QuestionSourceInfo;
